export enum QuickJumps {
  steps = 'steps',
  chapters = 'chapters',
  disabled = 'disabled'
}

import Vue from 'vue';
import Component from 'vue-class-component';
import TButton from '@/lib/form/button/Button.vue';
import TubeLabel from '@/lib/form/label/Label.vue';
import TInput from '@/lib/form/input/Input.vue';
import { Watch } from 'vue-property-decorator';

@Component({
  name: 'player-controls-mixin',
  props: [
    'duration',
    'currentTime',
    'status',
    'videoVolume',
    'muted',
    'mode',
    'steps',
    'chapters',
    'quickJump',
    'slide',
    'simple',
    'slideModeEnabled',
    'caption',
    'subtitleTracks',
    'initialSubtitleVisibility',
    'removeTrainnBranding'
  ],
  components: {
    TButton,
    TubeLabel,
    TInput
  }
})
export default class PlayerControlsMixin extends Vue {
  private volume = 0;
  private pipEnabled = true;
  private timerId!: any;
  private showCaption = false;
  private playbackRate = 1;
  private playbackRates!: number[];
  private showPopover = false;
  private subtitleLanguage = '';
  videoVolume!: number;
  duration!: number;
  currentTime!: number;
  status!: string;
  muted!: boolean;
  mode!: string;
  subtitleTracks!: any[];
  initialSubtitleVisibility!: boolean;
  steps!: any;
  chapters!: any;
  quickJump!: string;

  beforeCreate() {
    this.playbackRates = [1, 1.2, 1.5, 1.7, 2];
  }

  mounted() {
    this.volume = this.videoVolume;
    if (!('pictureInPictureEnabled' in document)) {
      this.pipEnabled = false;
    }
    window.addEventListener('keyup', this.keyboardShortcuts);
    document.addEventListener('click', this.hidePopover);
    this.showCaption = this.subtitleLanguage =
      (this.initialSubtitleVisibility &&
        !this.$isEmpty(this.subtitleTracks) &&
        this.subtitleTracks[0].lang) ||
      '';
  }

  get isChapter() {
    return this.quickJump == QuickJumps.chapters;
  }

  get quickJumpSteps() {
    if (this.$isEmpty(this.steps) || this.quickJump == QuickJumps.disabled) {
      return;
    }
    if (
      this.quickJump == QuickJumps.chapters &&
      !this.$isEmpty(this.chapters)
    ) {
      return this.chapters;
    }
    return this.steps;
  }

  get width(): string | false {
    if (!this.duration || !this.currentTime) {
      return '0';
    }
    return `${(this.currentTime / this.duration!) * 100}%`;
  }

  get isPaused() {
    return this.status == 'paused';
  }

  get isEnded() {
    return this.status == 'ended';
  }

  get formattedCurrentTime() {
    return this.$msToTime(this.currentTime * 1000);
  }

  get formattedDuration() {
    return this.$msToTime(this.duration * 1000);
  }

  get isMuted() {
    return this.volume == 0 || this.muted;
  }

  get volumelow() {
    return this.volume > 0 && this.volume < 0.5;
  }

  get isFullScreen() {
    return this.mode == 'fullscreen';
  }

  showSubtitleTrackOptions() {
    document.removeEventListener('click', this.hidePopover);
    this.showPopover = !this.showPopover;
    if (!this.showPopover) {
      return;
    }
    window.setTimeout(() => {
      document.addEventListener('click', this.hidePopover);
    });
  }

  hidePopover(event) {
    if (
      event &&
      event.target &&
      event.target.closest('.t-player__subtitle-control')
    ) {
      return;
    }
    this.showPopover = false;
  }

  setSubtitle(s) {
    this.$emit('setSubtitle', s);
    this.subtitleLanguage = (s && s.lang) || '';
    this.$nextTick(() => {
      this.showPopover = false;
    });
  }

  toggleCaption() {
    this.showCaption = !this.showCaption;
    this.subtitleLanguage = this.showCaption
      ? !this.$isEmpty(this.subtitleTracks) && this.subtitleTracks[0].lang
      : '';
    this.$emit('caption', this.showCaption);
  }

  @Watch('videoVolume')
  resyncVolume() {
    this.volume = this.videoVolume;
  }

  @Watch('volume')
  onVolumeUpdated() {
    this.$emit('volume', this.volume);
  }

  step(time): string | false {
    if (!this.duration || !time) {
      return '0';
    }
    return `${(time / this.duration!) * 100}%`;
  }

  play() {
    this.$emit('play');
  }

  pause() {
    this.$emit('pause');
  }

  enterSlideMode() {
    this.$emit('enterSlideMode');
  }

  seek(event) {
    if (!this.duration) {
      return;
    }
    const slider = event.currentTarget as HTMLElement;
    const rect = slider.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const seekTime = (x / slider.clientWidth) * this.duration!;
    this.$emit('seek', seekTime);
  }

  mute() {
    this.$emit('mute');
  }

  unmute() {
    this.$emit('unmute');
  }

  enterFullScreen() {
    this.$emit('fullscreen');
  }

  exitFullScreen() {
    this.$emit('defaultscreen');
  }

  exitSlideMode() {
    this.$emit('exitSlideMode');
  }

  togglePip() {
    this.$emit('togglePip');
  }

  debounce(func, delay) {
    clearTimeout(this.timerId);
    this.timerId = setTimeout(func, delay);
  }

  keyboardShortcuts(event) {
    const { key } = event;
    switch (key) {
      case 'k':
      case ' ':
        this.isPaused || this.isEnded ? this.play() : this.pause();
        event.stopPropagation();
        event.preventDefault();
        break;
      case 'm':
        this.muted ? this.unmute() : this.mute();
        break;
      case 'f':
        this.isFullScreen ? this.exitFullScreen() : this.enterFullScreen();
        break;
      case 'p':
        this.togglePip();
        break;
      case 'ArrowRight':
        // this.debounce(() => {
        this.$emit('forward');
        // }, 2000);
        break;
      case 'ArrowLeft':
        // this.debounce(() => {
        this.$emit('backward');
        // }, 2000);
        break;
    }
  }

  setPlaybackSpeed() {
    let index = this.playbackRates.indexOf(this.playbackRate);
    index = index < this.playbackRates.length - 1 ? ++index : 0;
    this.playbackRate = this.playbackRates[index];
    this.$emit('playbackRate', this.playbackRate);
  }

  destroyed() {
    window.removeEventListener('keyup', this.keyboardShortcuts);
    document.removeEventListener('click', this.hidePopover);
  }
}
